import React, { useState } from "react";
import "./MainDetail.css";

const MainDetail = ({ selectedBook, onTagClick }) => {
  const [isCitationVisible, setIsCitationVisible] = useState(false);
  const [citationStyle, setCitationStyle] = useState("MLA");

  const handleCiteClick = () => {
    setIsCitationVisible(true);
  };

  const handleCloseCitation = () => {
    setIsCitationVisible(false);
  };

  const handleCopyToClipboard = () => {
    const citationText = generateCitationText(citationStyle);

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(citationText)
        .then(() => {
          alert("Citation copied to clipboard!");
        })
        .catch((err) => {
          console.error("Could not copy text: ", err);
          alert("Failed to copy the citation text.");
        });
    } else {
      console.warn("Clipboard API not available");
      alert("Clipboard API not available. Please copy manually.");
    }
  };

  const generateCitationText = (style) => {
    const {
      "Book title": title,
      "Name as it appears": author,
      "Publication year": year,
    } = selectedBook;
    switch (style) {
      case "MLA":
        return `${author}. ${title}. ${year}.`;
      case "APA":
        return `${author} (${year}). ${title}.`;
      case "Chicago":
        return `${author}. ${year}. ${title}.`;
      default:
        return "";
    }
  };

  const generateCitation = (style) => {
    const citationText = generateCitationText(style);
    const [author, title, year] = citationText.split(/(?<=\.|\?)\s/);

    return (
      <>
        {author} <i>{title}</i> {year}
      </>
    );
  };

  const generateWorldCatSearchURL = () => {
    const { "Book title": title, "Name as it appears": author } = selectedBook;
    let url = `https://www.worldcat.org/search?q=`;

    if (title) {
      url += `ti:${encodeURIComponent(title)}+`;
    }

    if (author) {
      url += `au:${encodeURIComponent(author)}`;
    }

    return url;
  };

  const handleWorldCatSearch = () => {
    const worldCatURL = generateWorldCatSearchURL();
    window.open(worldCatURL, "_blank"); // Opens the WorldCat search in a new tab
  };

  const TagButton = ({ children, onClick }) => (
    <button
      onClick={onClick}
      style={{
        display: "flex",
        padding: "6px 12px",
        alignItems: "center",
        gap: "4px",
        borderRadius: "8px",
        border: "0.25px solid var(--Gray-Black, #00000090)",
        background: "transparent",
        cursor: "pointer",
        color: "#00000090",
      }}
    >
      {children}
    </button>
  );

  const bookCoverUrl = selectedBook.Bookcover.replace(".", "");

  return (
    <div className="book-detail">
      <img
        src={bookCoverUrl}
        alt={`Cover of ${selectedBook["Book title"]}`}
        className="book-detail-image"
        onError={(e) => {
          e.target.onerror = null;
          e.target.src = `${process.env.PUBLIC_URL}/placehold.svg`;
        }}
      />
      <div className="book-detail-info">
        <p className="bookTitle">{selectedBook["Book title"]}</p>
        <p className="NameNAuthor">{selectedBook["Name as it appears"]}</p>
        <p className="NameNAuthor">{selectedBook["Publication year"]}</p>

        <div className="book-detail-tags">
          {selectedBook && Array.isArray(selectedBook.Categories)
            ? selectedBook.Categories.map((category, index) => (
                <div key={index} className="tagbuttons">
                  <TagButton onClick={() => onTagClick(category, "Categories")}>
                    {category}
                  </TagButton>
                </div>
              ))
            : null}
        </div>

        <div className="book-detail-tags">
          {selectedBook && Array.isArray(selectedBook["Location/region"])
            ? selectedBook["Location/region"].map((location, index) => (
                <div key={index} className="tagbuttons">
                  <TagButton
                    onClick={() => onTagClick(location, "Location/region")}
                  >
                    {location}
                  </TagButton>
                </div>
              ))
            : null}
        </div>

        <div className="book-detail-tags">
          {selectedBook && Array.isArray(selectedBook["Major award(s)"])
            ? selectedBook["Major award(s)"].map((award, index) => (
                <div key={index} className="tagbuttons">
                  <TagButton
                    onClick={() => onTagClick(award, "Major award(s)")}
                  >
                    {award}
                  </TagButton>
                </div>
              ))
            : null}
        </div>
        <p className="Summary">{selectedBook.Summary}</p>

        <div className="actions">
          {/* Button for citation */}
          <TagButton onClick={handleCiteClick}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M15 15C15.3315 15 15.6494 14.8683 15.8839 14.6339C16.1183 14.3995 16.25 14.0815 16.25 13.75V10.6975C16.25 10.366 16.1183 10.048 15.8839 9.81362C15.6494 9.5792 15.3315 9.4475 15 9.4475H13.265C13.265 9.00875 13.2912 8.56875 13.3425 8.13C13.42 7.665 13.55 7.25125 13.73 6.89C13.9112 6.5275 14.1437 6.24375 14.4287 6.03625C14.7125 5.80375 15.0737 5.6875 15.5137 5.6875V3.75C14.79 3.75 14.1575 3.905 13.6137 4.215C13.0747 4.52186 12.6113 4.94556 12.2575 5.455C11.9024 6.01664 11.6407 6.63212 11.4825 7.2775C11.3231 7.9897 11.2451 8.7177 11.25 9.4475V13.75C11.25 14.0815 11.3817 14.3995 11.6161 14.6339C11.8505 14.8683 12.1685 15 12.5 15H15ZM7.49997 15C7.83149 15 8.14944 14.8683 8.38386 14.6339C8.61828 14.3995 8.74997 14.0815 8.74997 13.75V10.6975C8.74997 10.366 8.61828 10.048 8.38386 9.81362C8.14944 9.5792 7.83149 9.4475 7.49997 9.4475H5.76497C5.76497 9.00875 5.79122 8.56875 5.84247 8.13C5.91997 7.665 6.04997 7.25125 6.22997 6.89C6.41122 6.5275 6.64372 6.24375 6.92872 6.03625C7.21247 5.80375 7.57372 5.6875 8.01372 5.6875V3.75C7.28997 3.75 6.65747 3.905 6.11372 4.215C5.57471 4.52186 5.11128 4.94556 4.75747 5.455C4.40238 6.01664 4.14065 6.63212 3.98247 7.2775C3.82308 7.9897 3.74508 8.7177 3.74997 9.4475V13.75C3.74997 14.0815 3.88167 14.3995 4.11609 14.6339C4.35051 14.8683 4.66845 15 4.99997 15H7.49997Z"
                fill="#765623"
              />
            </svg>
          </TagButton>

          <TagButton>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
            >
              <g clipPath="url(#clip0_2090_572)">
                <path
                  d="M11 2.50007C10.9999 1.9135 11.2061 1.34559 11.5825 0.895681C11.9588 0.445776 12.4814 0.142533 13.0588 0.039007C13.6361 -0.0645194 14.2315 0.0382633 14.7408 0.329372C15.25 0.620481 15.6406 1.08138 15.8444 1.63143C16.0481 2.18148 16.0519 2.78565 15.8552 3.33824C15.6585 3.89084 15.2737 4.35666 14.7682 4.65421C14.2627 4.95177 13.6687 5.06211 13.0901 4.96592C12.5115 4.86974 11.9851 4.57316 11.603 4.12807L4.88502 7.24807C5.03946 7.73751 5.03946 8.26263 4.88502 8.75207L11.603 11.8721C12.0069 11.4024 12.5707 11.0994 13.1852 11.0217C13.7997 10.9441 14.4212 11.0973 14.9292 11.4518C15.4372 11.8062 15.7955 12.3366 15.9347 12.9402C16.0739 13.5438 15.9841 14.1775 15.6826 14.7186C15.3812 15.2598 14.8896 15.6697 14.3031 15.869C13.7166 16.0683 13.077 16.0429 12.5083 15.7975C11.9396 15.5521 11.4822 15.1043 11.2247 14.5409C10.9673 13.9775 10.9282 13.3386 11.115 12.7481L4.39702 9.62807C4.06446 10.0156 3.62117 10.2921 3.1268 10.4202C2.63243 10.5482 2.11069 10.5219 1.63176 10.3445C1.15283 10.1672 0.739697 9.8475 0.447928 9.42836C0.15616 9.00921 -0.000244141 8.51077 -0.000244141 8.00007C-0.000244141 7.48937 0.15616 6.99093 0.447928 6.57178C0.739697 6.15264 1.15283 5.8329 1.63176 5.65559C2.11069 5.47828 2.63243 5.45189 3.1268 5.57998C3.62117 5.70808 4.06446 5.9845 4.39702 6.37207L11.115 3.25207C11.0386 3.00874 10.9998 2.75514 11 2.50007Z"
                  fill="#765623"
                />
              </g>
              <defs>
                <clipPath id="clip0_2090_572">
                  <rect width="16" height="16" fill="white" />
                </clipPath>
              </defs>
            </svg>
          </TagButton>

          {/* Button for WorldCat search */}
          <TagButton onClick={handleWorldCatSearch}>
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <mask
                id="mask0_1431_35317"
                maskUnits="userSpaceOnUse"
                x="0"
                y="0"
                width="20"
                height="20"
              >
                <rect width="20" height="20" fill="#D9D9D9" />
              </mask>
              <g mask="url(#mask0_1431_35317)">
                <path
                  d="M4.16675 13.3334V9.16675C4.16675 8.93064 4.24661 8.73272 4.40633 8.573C4.56605 8.41328 4.76397 8.33342 5.00008 8.33342C5.23619 8.33342 5.43411 8.41328 5.59383 8.573C5.75355 8.73272 5.83341 8.93064 5.83341 9.16675V13.3334C5.83341 13.5695 5.75355 13.7674 5.59383 13.9272C5.43411 14.0869 5.23619 14.1667 5.00008 14.1667C4.76397 14.1667 4.56605 14.0869 4.40633 13.9272C4.24661 13.7674 4.16675 13.5695 4.16675 13.3334ZM9.16675 13.3334V9.16675C9.16675 8.93064 9.24661 8.73272 9.40633 8.573C9.56605 8.41328 9.76397 8.33342 10.0001 8.33342C10.2362 8.33342 10.4341 8.41328 10.5938 8.573C10.7536 8.73272 10.8334 8.93064 10.8334 9.16675V13.3334C10.8334 13.5695 10.7536 13.7674 10.5938 13.9272C10.4341 14.0869 10.2362 14.1667 10.0001 14.1667C9.76397 14.1667 9.56605 14.0869 9.40633 13.9272C9.24661 13.7674 9.16675 13.5695 9.16675 13.3334ZM2.50008 17.5001C2.26397 17.5001 2.06605 17.4202 1.90633 17.2605C1.74661 17.1008 1.66675 16.9029 1.66675 16.6667C1.66675 16.4306 1.74661 16.2327 1.90633 16.073C2.06605 15.9133 2.26397 15.8334 2.50008 15.8334H17.5001C17.7362 15.8334 17.9341 15.9133 18.0938 16.073C18.2536 16.2327 18.3334 16.4306 18.3334 16.6667C18.3334 16.9029 18.2536 17.1008 18.0938 17.2605C17.9341 17.4202 17.7362 17.5001 17.5001 17.5001H2.50008ZM14.1667 13.3334V9.16675C14.1667 8.93064 14.2466 8.73272 14.4063 8.573C14.5661 8.41328 14.764 8.33342 15.0001 8.33342C15.2362 8.33342 15.4341 8.41328 15.5938 8.573C15.7536 8.73272 15.8334 8.93064 15.8334 9.16675V13.3334C15.8334 13.5695 15.7536 13.7674 15.5938 13.9272C15.4341 14.0869 15.2362 14.1667 15.0001 14.1667C14.764 14.1667 14.5661 14.0869 14.4063 13.9272C14.2466 13.7674 14.1667 13.5695 14.1667 13.3334ZM17.5001 6.66675H2.41675C2.20841 6.66675 2.03133 6.59383 1.8855 6.448C1.73966 6.30217 1.66675 6.12508 1.66675 5.91675V5.45842C1.66675 5.30564 1.70494 5.17369 1.78133 5.06258C1.85772 4.95147 1.95841 4.86119 2.08341 4.79175L9.25008 1.20841C9.48619 1.0973 9.73619 1.04175 10.0001 1.04175C10.264 1.04175 10.514 1.0973 10.7501 1.20841L17.8751 4.77091C18.0279 4.84036 18.1424 4.94453 18.2188 5.08342C18.2952 5.2223 18.3334 5.36814 18.3334 5.52092V5.83342C18.3334 6.06953 18.2536 6.26744 18.0938 6.42717C17.9341 6.58689 17.7362 6.66675 17.5001 6.66675Z"
                  fill="#765623"
                />
              </g>
            </svg>
          </TagButton>
        </div>
      </div>

      {isCitationVisible && (
        <div className="citation-modal">
          <div className="citation-content">
            <button
              onClick={handleCloseCitation}
              className="close-citation-button"
            >
              X
            </button>
            <div className="citation-styles">
              <button
                onClick={() => setCitationStyle("MLA")}
                className={citationStyle === "MLA" ? "active" : ""}
              >
                MLA
              </button>
              <button
                onClick={() => setCitationStyle("APA")}
                className={citationStyle === "APA" ? "active" : ""}
              >
                APA
              </button>
              <button
                onClick={() => setCitationStyle("Chicago")}
                className={citationStyle === "Chicago" ? "active" : ""}
              >
                Chicago
              </button>
            </div>
            <div className="citation-right">
              <div className="citation-text">
                <p>{generateCitation(citationStyle)}</p>
              </div>
              <button
                onClick={handleCopyToClipboard}
                className="copy-citation-button"
              >
                Copy to Clipboard
              </button>
              <p>
                <b>
                  Remember to check citations for accuracy before including them
                  in your work!
                </b>
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MainDetail;
